import Phaser from 'phaser';
import npcsData from '@/data/npcs.json';
import { useUIStore } from '@/state/uiStore';
import { TEX } from '@/data/assetManifest';
import { isNpcSpriteKey } from '@/data/npcSpriteRegistry';
import { isPhaserPointerOverBlockingUI } from '@/ui/pointerBlock';

interface NpcTpl {
  id: string;
  name: string;
  role: string;
  map: string;
  x: number;
  y: number;
  color: string;
  dialog: string;
  sprite?: string;
  spriteFrame?: number;
}

const NPC_LIST: NpcTpl[] = npcsData.npcs as NpcTpl[];

export function getNpcsForMap(mapKey: string): NpcTpl[] {
  return NPC_LIST.filter((n) => n.map === mapKey);
}

export interface NpcOpts {
  scene: Phaser.Scene;
  id: string;
  name: string;
  role: string;
  x: number;
  y: number;
  color: number;
  dialog: string;
  sprite?: string;
  spriteFrame?: number;
}

export class NPC extends Phaser.GameObjects.Container {
  npcId: string;
  dialog: string;
  private sprite: Phaser.GameObjects.Sprite;
  private shadow: Phaser.GameObjects.Graphics;
  private nameTag: Phaser.GameObjects.Text;
  private roleTag: Phaser.GameObjects.Text;

  constructor(opts: NpcOpts) {
    super(opts.scene, opts.x, opts.y);
    this.npcId = opts.id;
    this.dialog = opts.dialog;

    // Real npc.png spritesheet (16x16, 3x4). Scale x2 → 32x32 footprint.
    // Tint by NPC color from npcs.json so each NPC is visually distinct.
    // Custom sprite/frame may be supplied via the map editor.
    const spriteKey = opts.sprite && opts.scene.textures.exists(opts.sprite)
      ? opts.sprite
      : TEX.townNpc;
    const tex = opts.scene.textures.get(spriteKey);
    const isHiRes = isNpcSpriteKey(spriteKey);
    const wantedFrame = opts.spriteFrame ?? (spriteKey === TEX.townNpc ? 7 : 0);
    const frame = tex.has(String(wantedFrame)) ? wantedFrame : 0;
    // Anchor sprite so its feet sit at container y=0 (origin bottom-center).
    this.sprite = new Phaser.GameObjects.Sprite(opts.scene, 0, 0, spriteKey, frame);
    this.sprite.setOrigin(0.5, 1);
    if (isHiRes) {
      // 128x128 hi-res NPC sheet -> render at ~64px tall, no tint (preserve art).
      this.sprite.setScale(0.5);
      const idleAnim = `${spriteKey}_idle`;
      if (opts.scene.anims.exists(idleAnim)) {
        this.sprite.play(idleAnim);
      }
    } else {
      this.sprite.setScale(2).setTint(opts.color);
      if (spriteKey === TEX.townNpc && opts.scene.anims.exists('npc_idle_down')) {
        this.sprite.play('npc_idle_down');
      }
    }

    const spriteH = this.sprite.displayHeight;
    const spriteW = this.sprite.displayWidth;
    const headTopY = -spriteH; // top of sprite, since origin is bottom-center

    // Shadow sits right at the feet (container origin).
    const shadowW = Math.max(20, spriteW * 0.55);
    this.shadow = new Phaser.GameObjects.Graphics(opts.scene);
    this.shadow.fillStyle(0x000000, 0.35);
    this.shadow.fillEllipse(0, -2, shadowW, Math.max(6, shadowW * 0.3));

    const roleLabel = opts.role === 'quest' ? '[Quest]'
      : opts.role === 'shop' ? '[Shop]'
      : opts.role === 'guild' ? '[Guild]'
      : opts.role === 'heal' ? '[Heal]'
      : opts.role === 'storage' ? '[Storage]'
      : opts.role === 'warp' ? '[Warp]'
      : opts.role === 'mail' ? '[Mail]'
      : '[NPC]';
    // Place name above head, role above name.
    this.nameTag = new Phaser.GameObjects.Text(opts.scene, 0, headTopY - 6, opts.name, {
      fontFamily: '"Philosopher", "Cinzel", serif', fontSize: '12px', color: '#ffffff', stroke: '#000', strokeThickness: 3,
    }).setOrigin(0.5, 1);

    this.roleTag = new Phaser.GameObjects.Text(opts.scene, 0, headTopY - 6 - this.nameTag.height - 2, roleLabel, {
      fontFamily: '"Philosopher", "Cinzel", serif', fontSize: '10px', color: '#ffd166', stroke: '#000', strokeThickness: 2,
    }).setOrigin(0.5, 1);

    this.add([this.shadow, this.sprite, this.roleTag, this.nameTag]);
    // Hit area: tightened to the visible character body so overlapping NPC
    // frames (especially the 128x128 hi-res sheets with lots of transparent
    // padding) don't steal clicks from neighbours behind them.
    let hitW: number;
    let hitH: number;
    let hitOffsetY: number;
    if (isHiRes) {
      // Visible character occupies roughly the centre ~45% wide and ~75% tall
      // of the 128x128 frame, with feet near the bottom.
      hitW = Math.round(spriteW * 0.5);
      hitH = Math.round(spriteH * 0.78);
      hitOffsetY = Math.round(spriteH * 0.05); // small gap above feet
    } else {
      hitW = spriteW;
      hitH = spriteH + 4;
      hitOffsetY = 0;
    }
    this.setSize(hitW, hitH);
    this.setInteractive(
      new Phaser.Geom.Rectangle(-hitW / 2, -hitH - hitOffsetY, hitW, hitH),
      Phaser.Geom.Rectangle.Contains,
    );
    this.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      if (isPhaserPointerOverBlockingUI(pointer)) return;
      useUIStore.getState().openNpcDialog({
        id: opts.id,
        name: opts.name,
        role: opts.role,
        dialog: opts.dialog,
      });
    });
    this.on('pointerover', () => opts.scene.input.setDefaultCursor('pointer'));
    this.on('pointerout', () => opts.scene.input.setDefaultCursor('default'));

    opts.scene.add.existing(this);
    // Y-sort with props/players so the NPC overlaps stalls/trees in front of them.
    this.setDepth(this.y);
  }

  destroy(fromScene?: boolean) {
    this.sprite.destroy();
    this.shadow.destroy();
    this.nameTag.destroy();
    this.roleTag.destroy();
    super.destroy(fromScene);
  }
}
